/**
 * Composes the toolbar's per-area action hooks into the single
 * `ToolbarActions` record the descriptor model consumes, plus the two AI-menu
 * handlers and the dialog toggles `MainToolbar` still has to render.
 *
 * The record is keyed by `ToolbarItemId`, so TypeScript fails the build if an
 * item is added to the model without an action — the descriptor list and the
 * handlers cannot drift apart.
 */
import { addBreadcrumb } from '../../crash-dialog/services/breadcrumbService';
import { useWorkspaceStore } from '../../workspace';
import { openVqeFromActiveTab } from '../../workspace/visual-query-editor/workspace/openVqeFromActiveTab';
import { openDashboardTab } from '../../workspace/dashboards/utils/openDashboardTab';
import { useTerminalStore } from '../../terminal';
import type { UseRestrictedFeatureDialogResult } from '../../browser-mode/hooks/useRestrictedFeatureDialog';
import type { Connection } from '../../../types/electron';
import type { ToolbarActions } from '../model/toolbarItemTypes';
import type { ToolbarActionContext } from './toolbarActionContext';
import { useToolbarQueryActions } from './useToolbarQueryActions';
import { useToolbarFileActions } from './useToolbarFileActions';
import { useToolbarExplorerActions } from './useToolbarExplorerActions';
import { useToolbarCompareActions } from './useToolbarCompareActions';
import { useToolbarDbaActions } from './useToolbarDbaActions';
import { useToolbarOracleActions } from './useToolbarOracleActions';

type MainToolbarActionDeps = {
  fallbackConnectionId: string | undefined;
  selectedConnectionId: string | undefined;
  selectedDatabase: string | undefined;
  connections: Connection[];
  restrictedDialog: UseRestrictedFeatureDialogResult;
};

export function useMainToolbarActions(ctx: ToolbarActionContext, deps: MainToolbarActionDeps) {
  const { fallbackConnectionId, selectedConnectionId, selectedDatabase, connections, restrictedDialog } = deps;
  const toggleTerminal = useTerminalStore((s) => s.togglePanel);

  const query = useToolbarQueryActions(ctx);
  const files = useToolbarFileActions(ctx, {
    fallbackConnectionId,
    selectedDatabase,
    connections,
    checkAndBlock: restrictedDialog.checkAndBlock,
  });
  const explorer = useToolbarExplorerActions(ctx);
  const compare = useToolbarCompareActions(ctx);
  const dba = useToolbarDbaActions(ctx, selectedConnectionId);
  const oracle = useToolbarOracleActions(ctx);

  const actions: ToolbarActions = {
    home: () => {
      addBreadcrumb('click', 'toolbar:home');
      ctx.track('toolbar:home');
      useWorkspaceStore.getState().ensureStartPage();
    },
    'new-query': query.newQuery,
    'visual-query': () => {
      addBreadcrumb('click', 'toolbar:visual-query');
      ctx.track('toolbar:visual_query');
      openVqeFromActiveTab(undefined, { source: 'toolbar' });
    },
    dashboards: () => {
      addBreadcrumb('click', 'toolbar:dashboards');
      ctx.track('toolbar:dashboards');
      openDashboardTab();
    },
    terminal: () => {
      if (restrictedDialog.checkAndBlock('terminal')) return;
      addBreadcrumb('click', 'toolbar:terminal');
      ctx.track('toolbar:terminal');
      toggleTerminal();
    },
    ...explorer.actions,
    ...files,
    ...compare.actions,
    ...dba,
    ...oracle,
  };

  return {
    actions,
    ai: {
      newQueryWithAi: query.newQueryWithAi,
      packageExplorerWithAi: query.packageExplorerWithAi,
    },
    dialogs: {
      tableDesigner: explorer.tableDesignerDialog,
      dataCompare: compare.dataCompareDialog,
      schemaCompare: compare.schemaCompareDialog,
    },
  };
}
